// pages/ManageTables.tsx — FINAL RTP FULLY INTEGRATED VERSION
// Real-time sync, optimistic delete, RTP warnings, toast feedback, color-coded RTP

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import useAdmin from '../hooks/useAdmin';
import { useConfirmModal } from '../utils/modal';
import ConfirmModal from '../../shared/components/ConfirmModal';
import { toast } from 'react-hot-toast'; // npm install react-hot-toast

interface Table {
  id: string;
  name: string;
  location?: string | null;
  manager_email?: string | null;
  current_rtp: number;
  payout_ratio: number;
  created_at: string;
  jackpot?: number;
}

interface SearchForm {
  search: string;
}

export default function ManageTables() {
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(false);
  const { token, fetchTables, deleteTable, error, setError, socket } = useAdmin();
  const { isOpen, message, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const { register, watch } = useForm<SearchForm>({ defaultValues: { search: '' } });
  const search = watch('search', '');

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchTables()
        .then((data: Table[]) => {
          setTables((data || []) as any[]);
          setLoading(false);
        })
        .catch((err: Error) => {
          setError(`Failed to load tables: ${err.message}`);
          setLoading(false);
        });
    }
  }, [token, fetchTables, setError]);

  // Real-time sync for tables + RTP/jackpot events
  useEffect(() => {
    if (!socket) return;

    socket.on('rtp-updated', (data: { tableId: string; rtp: number }) => {
      toast(`RTP updated for table ${data.tableId}: ${data.rtp}%`);
      setTables(prev =>
        prev.map(t => t.id === data.tableId ? { ...t, current_rtp: data.rtp } : t)
      );
    });

    socket.on('jackpot-win', (data: { tableId: string; amount: number }) => {
      toast.success(`Jackpot win on table ${data.tableId}: $${data.amount}`);
      setTables(prev =>
        prev.map(t => t.id === data.tableId ? { ...t, jackpot: (t.jackpot || 0) - data.amount } : t)
      );
    });

    socket.on('table-updated', () => {
      toast('Table list updated in real-time');
      fetchTables().then(data => setTables((data || []) as any[]));
    });

    socket.on('table-deleted', (data: { tableId: string }) => {
      toast.success(`Table deleted: ${data.tableId}`);
      setTables(prev => prev.filter(t => t.id !== data.tableId));
    });

    return () => {
      socket.off('rtp-updated');
      socket.off('jackpot-win');
      socket.off('table-updated');
      socket.off('table-deleted');
    };
  }, [socket, fetchTables]);

  const filtered = tables.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.manager_email?.toLowerCase().includes(search.toLowerCase())
  );

  const toNumber = (value: unknown, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const handleDelete = (table: Table) => {
    showConfirmModal(
      `Delete table "${table.name}" permanently? This will revoke all RTP control and jackpot data for this table. Cannot be undone.`,
      async () => {
        try {
          // Optimistic remove
          setTables(prev => prev.filter(t => t.id !== table.id));

          await deleteTable(table.id);
          toast.success(`Table "${table.name}" deleted`);
        } catch (err: any) {
          // Revert on error
          setTables(prev => [...prev, table]);
          setError(`Delete failed: ${err.message}`);
          toast.error(`Delete failed: ${err.message}`);
        }
      }
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Manage Tables</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search by table name or manager..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            {...register('search')}
            aria-label="Search tables"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-indigo-600" size={48} />
            <span className="ml-4 text-xl text-gray-700">Loading tables...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            No tables found {search ? `matching "${search}"` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <tr>
                  <th className="p-4 text-left font-semibold">Name</th>
                  <th className="p-4 text-left font-semibold">Location</th>
                  <th className="p-4 text-left font-semibold">Manager</th>
                  <th className="p-4 text-left font-semibold">RTP (%)</th>
                  <th className="p-4 text-left font-semibold">Payout Ratio</th>
                  <th className="p-4 text-left font-semibold">Jackpot ($)</th>
                  <th className="p-4 text-left font-semibold">Created</th>
                  <th className="p-4 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} className="border-b hover:bg-gray-50 transition">
                    <td className="p-4 font-medium" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.name || "") }} />
                    <td className="p-4 text-gray-600">{t.location || '—'}</td>
                    <td className="p-4 text-gray-600">{t.manager_email || 'None'}</td>
                    <td className="p-4 font-bold text-center" style={{
                      color: toNumber(t.current_rtp) >= 0.95 ? '#10b981' :
                             toNumber(t.current_rtp) >= 0.90 ? '#f59e0b' :
                             '#ef4444'
                    }}>
                      {(toNumber(t.current_rtp) * 100).toFixed(2)}%
                    </td>
                    <td className="p-4 text-center text-gray-700">{(toNumber(t.payout_ratio) * 100).toFixed(2)}%</td>
                    <td className="p-4 font-bold text-yellow-600 text-center">
                      ${toNumber(t.jackpot).toFixed(2)}
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      {new Date(t.created_at).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </td>
                    <td className="p-4">
                      <button
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-sm"
                        onClick={() => handleDelete(t)}
                        aria-label={`Delete table ${t.name}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isOpen}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
